import { Observable, map } from 'rxjs';
import { CONTROLLER_CONSTANTS } from '../../../shared/constants/controller.constants';
import { AdminFeedbackResponse, FeedbackRequestModel, MessageFeedback, ResolveFeedbackRequestModel } from '../interfaces/feedback.interface';
import { ENDPOINT_CONSTANTS } from '../../../shared/constants/endpoint.constants';
import { StreamingController } from '../controllers/streaming.controller';
import { GetChatHistoryByChatIdModel, GetChatHistoryByChatIdResponse } from '../interfaces/get-chat-history-by-chat-id.interface';
import { GetActiveChatHistoryResponse } from '../interfaces/get-active-chat-history.interface';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '../../../shared/services/base-http.service';
import { ChatHistoryResponse } from '../interfaces/chat-history-response.interface';
import { UpdateChatModel, UpdateChatResponse } from '../interfaces/update-chat.interface';
import { UserQueryModel, ChatBotResponse } from '../interfaces/user-query.interface';
import { ChatbotStreamRequest } from '../interfaces/chatbot-stream-request.interface';
import { VoiceNoteUploadResponse } from '../interfaces/voice-note.interface';

@Injectable({
  providedIn: 'root'
})
export class ChatService {

  constructor(
    private baseHttp: BaseHttpService,
    private streamingController: StreamingController
  ) { }

  getChatBotResponse(userQuery: UserQueryModel): Observable<ChatBotResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.CHATBOT_RESPONSE}`;
    return this.baseHttp.post<ChatBotResponse>(endpoint, userQuery);
  }

  getChatHistory(): Observable<ChatHistoryResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.GET_CHAT_HISTORY}`;
    return this.baseHttp.get<ChatHistoryResponse>(endpoint);
  }

  updateChat(updateChatModel: UpdateChatModel): Observable<UpdateChatResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.UPDATE_CHAT}`;
    return this.baseHttp.post<UpdateChatResponse>(endpoint, updateChatModel);
  }

  getChatHistoryByChatId(getChatHistoryByChatIdModel: GetChatHistoryByChatIdModel): Observable<GetChatHistoryByChatIdResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.GET_CHAT_HISTORY_BY_CHAT_ID}`;
    return this.baseHttp.post<GetChatHistoryByChatIdResponse>(endpoint, getChatHistoryByChatIdModel);
  }

  getActiveChatHistory(): Observable<GetActiveChatHistoryResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.GET_ACTIVE_CHAT_HISTORY}`;
    return this.baseHttp.get<GetActiveChatHistoryResponse>(endpoint);
  }

  deleteChat(chatId: string): Observable<any> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.DELETE_CHAT}?chatId=${chatId}`;
    return this.baseHttp.delete<any>(endpoint);
  }

  // Add streaming method
  getChatBotResponseStream(request: ChatbotStreamRequest): Observable<any> {
    return this.streamingController.getChatBotResponseStream(request);
  }

  submitAIFeedback(feedbackRequest: FeedbackRequestModel): Observable<any> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/SubmitAIFeedback`;
    return this.baseHttp.post<any>(endpoint, feedbackRequest);
  }

  getMessageFeedback(chatId: string, messageIndex: number): Observable<MessageFeedback> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/GetMessageFeedback?chatId=${chatId}&messageIndex=${messageIndex}`;
    return this.baseHttp.get<MessageFeedback>(endpoint);
  }

  getAllFeedbacks(): Observable<AdminFeedbackResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.GET_ALL_FEEDBACKS}`;
    return this.baseHttp.get<AdminFeedbackResponse>(endpoint);
  }

  resolveFeedback(feedbackRequest: ResolveFeedbackRequestModel): Observable<any> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.RESOLVE_FEEDBACK}`;
    return this.baseHttp.post<any>(endpoint, feedbackRequest);
  }
  saveChat(chatId: string | null, messages: any[],ChatLable:string |null): Observable<any> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/${ENDPOINT_CONSTANTS.SAVE_CHAT}`;
    return this.baseHttp.post<any>(endpoint, {
      chatId,
      messagesJson: JSON.stringify(messages),
      ChatLable
    });
  }

  uploadVoiceNote(blob: Blob, durationSeconds: number): Observable<VoiceNoteUploadResponse> {
    const form = new FormData();
    const type = (blob.type || 'audio/webm').split(';')[0];
    const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
    form.append('audio', blob, `voice-note.${ext}`);
    form.append('durationSeconds', String(Math.round(durationSeconds)));
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/VoiceNote`;
    return this.baseHttp.postFile<VoiceNoteUploadResponse>(endpoint, form);
  }

  getVoiceNote(id: string): Observable<Blob> {
    const endpoint = `/${CONTROLLER_CONSTANTS.CHAT}/VoiceNote/${id}`;
    return this.baseHttp.getBlobWithResponse(endpoint).pipe(
      map((response) => {
        if (!response.body) {
          throw new Error('Voice note was empty');
        }
        return response.body;
      })
    );
  }
}
